<template>
  <div class="forgot-password">
    <div class="landing-page-wrapper">
      <div class="landing-page-content">
        <div class="forgot-password">
          <h1 class="page-title color-1-100">
            <div class="content">
              Forgot password?
            </div>
          </h1>
          <p class="page-description color-2-77">
            Please enter the email address associated with your Clear Assured account.<br/>
            We will send you a reset password link and instructions on how to change it.
          </p>

          <form class="forgot-password--form" v-on:submit.prevent="sendFormRequest()">
            <div class="landing-page-login-group"
                 :class="[{ 'active': formInfo.email !== '' || emailFocus, 'has-error': errors.has('Email') }]">
              <label for="register-email">Email</label>
              <input id="register-email"
                     :class="[{ 'border-1-20': formInfo.email !== '' || emailFocus }]"
                     data-lpignore="true"
                     type="email"
                     name="Email"
                     placeholder="Type in your e-mail address..."
                     v-validate="'required|email'"
                     @focus="emailFocus = true"
                     @blur="emailFocus = false"
                     v-model="formInfo.email" />
            </div>
            <button type="submit">Send me reset link</button>
          </form>

          <div class="landing-page-login-info">
            Having trouble accessing your account several times? <br/>
            <router-link :to="{ name: 'contact' }">Get in touch</router-link> and tell us about your problem, we’ll do our best to help you.
          </div>

          <div class="tcc-form-message"
               aria-live="assertive">
            <div v-if="this.formStatus.submitting" class="visually-hidden">
              Submitting
            </div>
            <template v-else>
              <template v-if="formStatus.shown">
                <h2 v-if="formStatus.title !== ''">
                  <template v-if="formStatus.success">
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="circle-check" class="svg-inline--fa fa-circle-check" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                      <path fill="currentColor" d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM371.8 211.8l-128 128C238.3 345.3 231.2 348 224 348s-14.34-2.719-19.81-8.188l-64-64c-10.91-10.94-10.91-28.69 0-39.63c10.94-10.94 28.69-10.94 39.63 0L224 280.4l108.2-108.2c10.94-10.94 28.69-10.94 39.63 0C382.7 183.1 382.7 200.9 371.8 211.8z"></path>
                    </svg>
                  </template>
                  <template v-else>
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="circle-xmark" class="svg-inline--fa fa-circle-xmark" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                      <path fill="currentColor" d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256C397.4 512 512 397.4 512 256S397.4 0 256 0zM336.1 303c9.375 9.375 9.375 24.56 0 33.94c-9.381 9.381-24.56 9.373-33.94 0L256 289.9l-47.03 47.03c-9.381 9.381-24.56 9.373-33.94 0c-9.375-9.375-9.375-24.56 0-33.94l47.03-47.03L175 208.1c-9.375-9.375-9.375-24.56 0-33.94s24.56-9.375 33.94 0L256 222.1l47.03-47.03c9.375-9.375 24.56-9.375 33.94 0s9.375 24.56 0 33.94l-47.03 47.03L336.1 303z"></path>
                    </svg>
                  </template>
                  {{ formStatus.title }}
                </h2>
                <p v-if="formStatus.body !== ''">
                  {{ formStatus.body }}
                </p>
              </template>
              <template v-else-if="formStatus.submitted && this.errors.items && this.errors.items.length">
                <h2>
                  <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="circle-exclamation" class="svg-inline--fa fa-circle-exclamation" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path fill="currentColor" d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM232 152C232 138.8 242.8 128 256 128s24 10.75 24 24v128c0 13.25-10.75 24-24 24S232 293.3 232 280V152zM256 400c-17.36 0-31.44-14.08-31.44-31.44c0-17.36 14.07-31.44 31.44-31.44s31.44 14.08 31.44 31.44C287.4 385.9 273.4 400 256 400z"></path>
                  </svg>
                  Please fill in the required information
                </h2>
                <p>Please fill in all the mandatory fields - they are marked with an asterisk.</p>
              </template>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'forgot-password',
  data () {
    return {
      resetInProgress: false,
      formStatus: {
        shown: false,
        success: false,
        title: '',
        body: '',
        submitting: false,
        submitted: false
      },
      emailFocus: false,
      formInfo: {
        email: ''
      }
    }
  },
  methods: {
    sendFormRequest () {
      let self = this
      this.formStatus.submitting = true
      this.formStatus.submitted = true
      this.$validator.validateAll().then((valid) => {
        if (!valid) {
          this.formStatus.submitting = false
          return
        }
        this.resetInProgress = true
        this.$http.post(api.URLS.forgotPassword, JSON.stringify(this.formInfo))
          .then((res) => {
            this.formStatus.success = true
            this.formStatus.title = 'Reset link successfully sent.'
            this.formStatus.body = 'If your email is in our database, you’ll receive an email shortly with instructions to reset your password.'
            this.formStatus.shown = true
            this.formInfo.email = ''
            this.resetInProgress = false
            setTimeout(function () {
              self.errors.clear()
              self.formStatus.submitting = false
            }, 100)
          })
          .catch((err) => {
            this.resetInProgress = false
            this.formStatus.success = false
            this.formStatus.title = err.body
            this.formStatus.body = ''
            this.formStatus.shown = true
            this.formStatus.submitting = false
          })
      })
    }
  },
  created () {
    document.title = 'Clear Assured – forgot password'
  }
}
</script>

<style src="@/assets/scss/main/landing-page.scss" lang="scss" scoped></style>



// WEBPACK FOOTER //
// ForgotPassword.vue?4e2ea479